(() => {
  if (customElements.get('collection-load-more')) return;
  // Declared at the top of the IIFE so connectedCallback (which fires synchronously
  // during customElements.define) can access it without hitting a temporal dead zone.
  let fetchTotalController = null;
  let collectionSyncGeneration = 0;

  class CollectionLoadMore extends HTMLElement {
    _isStale() {
      return this._syncGen !== collectionSyncGeneration;
    }

    _inStockFilterActive() {
      if (this.dataset.inStockFilter === 'true') return true;
      const list = document.getElementById(this.dataset.productListId);
      if (list?.dataset.inStockFilter === 'true') return true;
      return new URLSearchParams(window.location.search).get('filter.v.availability') === '1';
    }

    connectedCallback() {
      this._syncGen = collectionSyncGeneration;
      this._button = this.querySelector('.load-more-button');
      this._spinner = this.querySelector('.load-more-spinner');
      this._loading = false;
      this._autoFilling = false;
      // Holds sold-out cards removed from the DOM until the last page is reached,
      // at which point they are all flushed to the end of the container.
      this._soldOutBuffer = [];
      // Metafield-based reordering is only active when the collection uses manual sort.
      // For price / alpha / date sorts, Shopify's server-side order must be preserved.
      this._enableReorder = this.dataset.enableReorder === 'true';
      this._colorSplit = this.dataset.colorSplit === 'true';

      if (this._button) {
        this._button.addEventListener('click', () => this._loadMore());
      }

      const currentPage = this._getCurrentPage();
      this._backfilling = currentPage > 1;
      this._storedViewedCount = currentPage > 1 ? this._readStoredViewedCount() : 0;

      // Hide any color cards whose position is beyond this page's natural range.
      // Only applies in manual sort on page 1 — on page 2+ (direct URL), the server-rendered
      // cards are the current page's content and must not be deferred; the Load More path
      // never defers appended pages either, so this keeps both paths consistent.
      if (this._colorSplit && this._enableReorder && currentPage === 1) this._deferEarlyCards({ updateProgress: true });

      // Extract sold-out cards from the initial server-rendered page into the buffer.
      // Must run after _deferEarlyCards so deferred sold-out cards are not extracted yet.
      const curList = document.getElementById(this.dataset.productListId);
      if (curList) {
        this._extractSoldOutCards(curList);
        if (currentPage === 1) this._updateProgressBar(curList);
        this._positionGridMedia(curList);
        // Single-page collections (page 1, no more pages) — flush sold-out to the end now.
        // Skip on page 2+ landings; _backfillPages handles finalize after all pages load.
        if (currentPage === 1 && !this.dataset.nextUrl) this._finalizeCollection(curList);
      }

      // Toolbar uses collection.products_count (parent products); sync to color-card total immediately
      // when we already know it (single-page progress bar or rendered card count).
      if (this._colorSplit && this.dataset.productCountId) {
        const progressBar = this.dataset.progressBarId
          ? document.getElementById(this.dataset.progressBarId)
          : null;
        if (progressBar && !progressBar.classList.contains('products-progress-bar--loading')) {
          this._syncToolbarCount(parseInt(progressBar.dataset.total, 10) || 0);
        } else if (!this.dataset.nextUrl && curList) {
          this._syncToolbarCount(curList.querySelectorAll('product-card').length);
        }
      }

      // Fetch the true color-split total asynchronously and update the progress bar.
      // A module-level AbortController lets facet:update cancel any in-flight walk from
      // before the filter was applied, preventing stale totals from winning the race.
      if (this._colorSplit && this.dataset.progressBarId) {
        fetchTotalController = new AbortController();
        this._fetchTotalSignal = fetchTotalController.signal;
        // Show skeleton on the toolbar count while the async fetch runs
        const countEl = document.getElementById(this.dataset.productCountId);
        if (countEl) countEl.classList.add('collection-toolbar__products-count--loading');
        this._fetchColorCardTotal();
      }

      // When landing on ?page=N (N > 1) directly, auto-fetch all earlier pages
      // and prepend them so the visitor sees the full cumulative list.
      // For page 1, auto-fill is triggered here; for page > 1 it runs at the end of _backfillPages.
      if (currentPage > 1) {
        // Hide the list before backfill to prevent showing page N before page 1.
        // _backfillPages will reveal it as soon as page 1 is inserted.
        if (curList) curList.style.visibility = 'hidden';

        this._backfillPages(currentPage);
      } else if (curList) {
        // Page 1: check if the initial batch meets the minimum visible threshold
        this._autoFillIfNeeded(curList);
      }
    }

    async _fetchColorCardTotal() {
      const syncGen = this._syncGen;
      const progressBar = document.getElementById(this.dataset.progressBarId);
      if (!progressBar) return;

      try {
        // Build the first page URL — preserve all active filter params, strip ?page=
        const firstUrl = new URL(window.location.href);
        firstUrl.searchParams.set('view', 'color-count-api');
        firstUrl.searchParams.delete('page');

        // Walk all paginated pages and accumulate the color-card count
        let total = 0;
        let nextUrl = firstUrl.href;

        while (nextUrl) {
          if (syncGen !== collectionSyncGeneration) return;

          const resp = await fetch(nextUrl, { signal: this._fetchTotalSignal });
          if (!resp.ok) break;

          const data = JSON.parse(await resp.text());
          if (syncGen !== collectionSyncGeneration) return;

          total += data.count || 0;
          nextUrl = data.nextUrl || null;
        }

        if (syncGen !== collectionSyncGeneration) return;

        progressBar.dataset.total = total;

        const ofTotalEl = progressBar.querySelector('.products-progress-bar__of-total');
        if (ofTotalEl) ofTotalEl.textContent = total;

        const track = progressBar.querySelector('.products-progress-bar__track');
        if (track) track.setAttribute('aria-valuemax', total);

        const curList = document.getElementById(this.dataset.productListId);
        if (curList && !this._backfilling) this._updateProgressBar(curList);

        this._syncToolbarCount(total);
      } catch (e) {
        // Silently fall back to the server-rendered approximation
      } finally {
        if (syncGen !== collectionSyncGeneration) return;

        progressBar.classList.remove('products-progress-bar--loading');
        const countEl = document.getElementById(this.dataset.productCountId);
        if (countEl) countEl.classList.remove('collection-toolbar__products-count--loading');
      }
    }

    // Hide color cards whose data-position exceeds the natural card count for this page.
    // These are colors that were physically pulled to an early Shopify page because
    // another color of the same product has an early position. They should only
    // become visible once enough cards have been loaded to reach their position.
    _deferEarlyCards({ updateProgress = true } = {}) {
      const naturalCount = parseInt(this.dataset.pageNaturalCount || '0', 10);
      if (!naturalCount) return;
      const curList = document.getElementById(this.dataset.productListId);
      if (!curList) return;
      curList.querySelectorAll('product-card[data-position]').forEach(card => {
        const pos = parseInt(card.dataset.position, 10);
        if (pos > naturalCount) {
          card.hidden = true;
          card.dataset.deferred = '1';
        }
      });
      // Immediately correct the progress bar — deferred cards must not count as viewed
      if (updateProgress) this._updateProgressBar(curList);
    }

    // After each batch of new cards is added, reveal all deferred cards.
    // A positional threshold cannot work here: a deferred card at position N is itself
    // counted in totalLoaded, so totalLoaded can never arithmetically reach N. This mirrors
    // the refresh path (Bug 1 fix) where _deferEarlyCards never runs on backfilled HTML —
    // all page-1 cards are immediately visible on refresh, so Load More must match.
    // _deferEarlyCards still keeps the initial page-1 display clean; cards are unhidden
    // here on the first Load More, exactly as they would be visible on a direct URL refresh.
    _revealDeferredCards(curList) {
      curList.querySelectorAll('product-card[data-deferred]').forEach(card => {
        card.hidden = false;
        delete card.dataset.deferred;
      });
    }

    // Set each .collection-grid-media[data-position] element so it appears after the
    // Nth visible in-stock product. Manual sort uses CSS order (position × 2 on cards);
    // other sorts insert the banner in the DOM after the Nth in-stock card.
    _getInStockCards(curList) {
      const cards = [...curList.querySelectorAll('product-card:not([hidden]):not([data-deferred]):not([data-sold-out="true"])')];
      if (this._enableReorder) {
        cards.sort((a, b) => parseInt(a.style.order || '0', 10) - parseInt(b.style.order || '0', 10));
      }
      return cards;
    }

    _showGridMediaAt(media, nthCard) {
      const cardOrder = parseInt(nthCard.style.order || '0', 10);
      if (this._enableReorder) {
        // Always use CSS order for manual sort — matches pre-commit behaviour and keeps
        // sold-out cards (order +200000) at the bottom of the visual grid.
        media.style.order = String(cardOrder + 1);
      } else {
        media.style.removeProperty('order');
        nthCard.insertAdjacentElement('afterend', media);
      }
      media.style.display = '';
      media.style.transition = 'opacity 0.25s ease';
      requestAnimationFrame(() => { media.style.opacity = '1'; });
    }

    _positionGridMedia(curList) {
      // Deduplicate: absolute_card_index restarts on each Shopify page render, so the same
      // media item can appear multiple times when pages are merged via load-more/backfill.
      // For each data-position value, keep the first occurrence and remove the rest.
      const seenPositions = new Set();
      [...curList.querySelectorAll('.collection-grid-media[data-position]')].forEach(media => {
        if (seenPositions.has(media.dataset.position)) {
          curList.removeChild(media);
        } else {
          seenPositions.add(media.dataset.position);
        }
      });

      const mediaItems = [...curList.querySelectorAll('.collection-grid-media[data-position]')];
      if (!mediaItems.length) return;

      const inStockCards = this._getInStockCards(curList);
      mediaItems.forEach(media => {
        const pos = parseInt(media.dataset.position, 10);
        if (isNaN(pos) || pos < 1) return;
        const nthCard = inStockCards[pos - 1];
        // Not enough in-stock products loaded yet — stay hidden, try again on next batch
        if (!nthCard) return;
        this._showGridMediaAt(media, nthCard);
      });
    }

    // Silently fetch and process additional pages until at least data-min-visible
    // in-stock products are visible in curList, or no more pages exist.
    // A separate _autoFilling flag prevents re-entrance and blocks concurrent _loadMore calls.
    async _autoFillIfNeeded(curList) {
      if (this._autoFilling || !this.dataset.nextUrl) return;

      const minVisible = parseInt(this.dataset.minVisible || '30', 10);
      const MAX_FETCHES = 10; // safety ceiling against infinite loops

      const visibleCount = () =>
        curList.querySelectorAll('product-card:not([hidden]):not([data-deferred])').length;
      if (visibleCount() >= minVisible) return; 
      this._autoFilling = true;
      this._spinner?.removeAttribute('hidden');

      try {
        let fetches = 0;
        while (fetches++ < MAX_FETCHES && visibleCount() < minVisible && this.dataset.nextUrl) {
          if (this._isStale()) break;

          const url = this.dataset.nextUrl;
          const resp = await fetch(url);
          if (!resp.ok) break;
          if (this._isStale()) break;

          const html = await resp.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newList = doc.getElementById(this.dataset.productListId);
          if (!newList) break;
          if (this._isStale()) break;

          const useStagger = this.dataset.stagger === 'true';
          const startIdx = [...curList.children].filter(
            el => !el.classList.contains('collection-grid-media')
          ).length;

          [...newList.children].forEach((card, i) => {
            if (useStagger) card.style.setProperty('--animation-order', startIdx + i);
            card.removeAttribute('reveal-on-scroll');
            curList.appendChild(card);
          });

          this._revealDeferredCards(curList);
          this._extractSoldOutCards(curList);
          const viewed = this._updateProgressBar(curList);
          this._positionGridMedia(curList);
          history.replaceState({}, '', url);
          this._storeViewedCount(url, viewed);

          const nextComponent = doc.querySelector('collection-load-more');
          if (nextComponent && nextComponent.dataset.nextUrl) {
            this.dataset.nextUrl = nextComponent.dataset.nextUrl;
          } else {
            // Last page reached during auto-fill
            delete this.dataset.nextUrl;
            this.hidden = true;
            this._finalizeCollection(curList);
            break;
          }
        }
      } catch (e) {
        console.error('[LoadMore] auto-fill error:', e);
      } finally {
        this._autoFilling = false;
        this._spinner?.setAttribute('hidden', '');
      }
    }

    // Remove sold-out cards from the container into the buffer.
    // Skips cards that are still deferred — those will be extracted once revealed.
    _extractSoldOutCards(curList) {
      const inStockOnly = this._inStockFilterActive();
      curList.querySelectorAll('product-card[data-sold-out="true"]:not([data-deferred])').forEach(card => {
        curList.removeChild(card);
        if (!inStockOnly) {
          this._soldOutBuffer.push(card);
        }
      });
    }

    // Append all buffered sold-out cards to the end of the container with a fade-in
    // animation, then clear the buffer and re-sync the progress bar.
    _flushSoldOutBuffer(curList) {
      if (this._inStockFilterActive()) {
        this._soldOutBuffer = [];
        return;
      }
      if (!this._soldOutBuffer.length) return;
      const fragment = document.createDocumentFragment();
      this._soldOutBuffer.forEach(card => fragment.appendChild(card));
      curList.appendChild(fragment);
      [...curList.querySelectorAll('product-card[data-sold-out="true"]')].forEach((card, i) => {
        card.animate(
          [
            { opacity: '0', transform: 'translateY(20px)' },
            { opacity: '1', transform: 'translateY(0)' }
          ],
          { duration: 200, delay: i * 30, easing: 'ease-in-out', fill: 'both' }
        );
      });
      this._soldOutBuffer = [];
      this._updateProgressBar(curList);
      this._positionGridMedia(curList);
    }

    // Called once when the collection has no more pages to load.
    // Force-reveals any cards still deferred — in filtered collections data-position values
    // are global (e.g. 220) but totalLoaded is the filtered count (e.g. 71), so cards at
    // positions above the filtered total are permanently deferred without this step.
    // After revelation, extracts + flushes sold-out cards and re-syncs the UI.
    _finalizeCollection(curList) {
      curList.querySelectorAll('product-card[data-deferred]').forEach(card => {
        card.hidden = false;
        delete card.dataset.deferred;
      });
      this._extractSoldOutCards(curList); // catch newly visible sold-out cards
      this._flushSoldOutBuffer(curList);  // flush to end, calls _updateProgressBar + _positionGridMedia
      this._updateProgressBar(curList);   // re-sync in case buffer was empty (_flushSoldOutBuffer skips it)
      this._positionGridMedia(curList);   // reposition grid media after all products are revealed
    }

    // Fetch pages 1…(currentPage-1) and insert them before the server-rendered cards
    async _backfillPages(currentPage) {
      const curList = document.getElementById(this.dataset.productListId);
      if (!curList) return;

      // Fixed anchor = first card of the server-rendered page (page N).
      // Inserting earlier pages before this same anchor gives the correct order:
      // page-1 cards, then page-2 cards, …, then page-N cards.
      const anchor = curList.firstElementChild || null;

      for (let page = 1; page < currentPage; page++) {
        const url = new URL(window.location.href);
        if (page === 1) {
          url.searchParams.delete('page');
        } else {
          url.searchParams.set('page', page);
        }

        try {
          const resp = await fetch(url.href);
          if (!resp.ok) continue;

          const doc = new DOMParser().parseFromString(await resp.text(), 'text/html');
          const newList = doc.getElementById(this.dataset.productListId);

          if (newList) {
            [...newList.children].forEach(card => {
              card.removeAttribute('reveal-on-scroll');
              curList.insertBefore(card, anchor);
            });

            // Extract sold-outs from this backfilled batch before revealing the list.
            this._extractSoldOutCards(curList);

            // Reveal the list as soon as the first batch (page 1) is inserted.
            // All later batches will appear naturally since the list is already visible.
            if (curList.style.visibility === 'hidden') {
              curList.style.visibility = '';
            }
          }
        } catch (e) {
          console.error('[LoadMore] backfill failed for page', page, e);
          // On error, still reveal so the user isn't stuck with a blank list
          if (curList.style.visibility === 'hidden') {
            curList.style.visibility = '';
          }
        }
      }

      // Reveal any deferred cards whose position is now within loaded range
      this._revealDeferredCards(curList);
      // Extract any sold-out cards that were just revealed
      this._extractSoldOutCards(curList);
      this._backfilling = false;
      // Sync the progress bar after reveal so revealed cards are included in the count
      this._updateProgressBar(curList);
      // Position grid media items based on the current visible product order
      this._positionGridMedia(curList);
      // If this is the last page (no further pages to load), finalize the collection
      // (force-reveal deferred cards, flush sold-out, sync UI).
      // Otherwise check if more pages need to be auto-fetched to meet the minimum visible threshold.
      if (!this.dataset.nextUrl) {
        this._finalizeCollection(curList);
      } else {
        await this._autoFillIfNeeded(curList);
      }
    }

    _getCurrentPage() {
      const urlPage = parseInt(new URL(window.location.href).searchParams.get('page') || '1', 10);
      const datasetPage = parseInt(this.dataset.currentPage || '1', 10);

      return Math.max(
        Number.isNaN(urlPage) ? 1 : urlPage,
        Number.isNaN(datasetPage) ? 1 : datasetPage
      );
    }

    _getProgressStorageKey(url = window.location.href) {
      const progressUrl = new URL(url, window.location.href);
      progressUrl.hash = '';

      return `collection-load-more:viewed:${progressUrl.pathname}${progressUrl.search}`;
    }

    _readStoredViewedCount() {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this._getProgressStorageKey()) || 'null');
        if (!stored || stored.expires < Date.now()) return 0;

        return parseInt(stored.viewed, 10) || 0;
      } catch (e) {
        return 0;
      }
    }

    _storeViewedCount(url, viewed) {
      if (!viewed) return;

      try {
        const storedViewed = Math.max(this._storedViewedCount || 0, viewed);
        sessionStorage.setItem(
          this._getProgressStorageKey(url),
          JSON.stringify({
            viewed: storedViewed,
            expires: Date.now() + 30 * 60 * 1000
          })
        );
        this._storedViewedCount = storedViewed;
      } catch (e) {
        // Storage can be unavailable in some privacy modes; the DOM count remains the fallback.
      }
    }

    _syncToolbarCount(total) {
      if (this._isStale()) return;

      const countEl = document.getElementById(this.dataset.productCountId);
      if (!countEl || countEl.dataset.count == null) return;

      const countStr = String(Math.max(0, total));
      const prevCount = countEl.dataset.count;
      if (prevCount === countStr) return;

      countEl.textContent = countEl.textContent.replace(prevCount, countStr);
      countEl.dataset.count = countStr;
    }

    // Shared progress-bar updater used by both backfill and load-more
    _updateProgressBar(curList) {
      if (this._isStale()) return;
      const progressBarId = this.dataset.progressBarId;
      if (!progressBarId) return;

      const progressBar = document.getElementById(progressBarId);
      if (!progressBar) return;

      const total = parseInt(progressBar.dataset.total, 10);
      if (!Number.isFinite(total) || total < 0) return;

      const productCount = curList.querySelectorAll('product-card:not([hidden]):not([data-deferred])').length;
      const viewed = total > 0
        ? Math.min(Math.max(productCount, this._storedViewedCount || 0), total)
        : 0;
      const pct = total > 0 ? (viewed / total) * 100 : 0;

      const viewedEl = progressBar.querySelector('.products-progress-bar__viewed');
      const track = progressBar.querySelector('.products-progress-bar__track');
      const fill = progressBar.querySelector('.products-progress-bar__fill');
      if (viewedEl) viewedEl.textContent = viewed;
      if (track) track.setAttribute('aria-valuenow', viewed);
      if (fill) fill.style.width = pct + '%';

      return viewed;
    }

    async _loadMore() {
      if (this._loading || this._autoFilling) return;

      const nextUrl = this.dataset.nextUrl;
      if (!nextUrl) return;

      const loadedUrl = nextUrl;

      this._loading = true;
      this._button.setAttribute('disabled', '');
      this._button.classList.add('loading');
      this._spinner.removeAttribute('hidden');

      try {
        const resp = await fetch(nextUrl);
        if (!resp.ok) throw new Error(`[LoadMore] HTTP ${resp.status}`);

        const html = await resp.text();
        if (this._isStale()) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const listId = this.dataset.productListId;
        const newList = doc.getElementById(listId);
        const curList = document.getElementById(listId);
        let viewed;

        if (newList && curList) {
          if (this._isStale()) return;

          const useStagger = this.dataset.stagger === 'true';
          const startIdx = [...curList.children].filter(el => !el.classList.contains('collection-grid-media')).length;
          const newCards = [...newList.children];

          newCards.forEach((card, i) => {
            if (useStagger) card.style.setProperty('--animation-order', startIdx + i);
            card.removeAttribute('reveal-on-scroll');
            curList.appendChild(card);
          });

          newCards.forEach((card, i) => {
            card.animate(
              [
                { opacity: '0', transform: 'translateY(20px)' },
                { opacity: '1', transform: 'translateY(0)' }
              ],
              { duration: 200, delay: 400 + i * 50, easing: 'ease-in-out', fill: 'both' }
            );
          });

          this._revealDeferredCards(curList);
          this._extractSoldOutCards(curList);
          viewed = this._updateProgressBar(curList);
          this._positionGridMedia(curList);
        }

        history.replaceState({}, '', loadedUrl);
        this._storeViewedCount(loadedUrl, viewed);

        // Advance to the next-next URL, or hide the button when done
        const nextComponent = doc.querySelector('collection-load-more');
        if (nextComponent && nextComponent.dataset.nextUrl) {
          this.dataset.nextUrl = nextComponent.dataset.nextUrl;
          // Auto-fill more pages if the current batch is still below the visible threshold
          await this._autoFillIfNeeded(curList);
          this._button.removeAttribute('disabled');
        } else {
          this.hidden = true;
          // Last page reached — finalize (force-reveal deferred, flush sold-out, sync UI)
          const curList = document.getElementById(this.dataset.productListId);
          if (curList) this._finalizeCollection(curList);
        }
      } catch (err) {
        console.error(err);
        this._button.removeAttribute('disabled');
      } finally {
        this._loading = false;
        this._spinner.setAttribute('hidden', '');
        this._button.classList.remove('loading');
      }
    }
  }

  customElements.define('collection-load-more', CollectionLoadMore);

  // Invalidate in-flight count fetches and load-more work when filters change.
  document.addEventListener('facet:update', () => {
    collectionSyncGeneration++;
    if (fetchTotalController) fetchTotalController.abort();
    fetchTotalController = null;
  });

  function inStockFilterActive() {
    const loadMoreEl = document.querySelector('collection-load-more');
    if (loadMoreEl?.dataset.inStockFilter === 'true') return true;
    const productList = document.querySelector('[id^="product-list-"]');
    if (productList?.dataset.inStockFilter === 'true') return true;
    return new URLSearchParams(window.location.search).get('filter.v.availability') === '1';
  }

  // Move sold-out cards to the end of the product list (used when CSS order is not active).
  function relocateSoldOutCardsToEnd(productList) {
    const soldOutCards = [...productList.querySelectorAll('product-card[data-sold-out="true"]:not([data-deferred])')];
    soldOutCards.forEach(card => productList.appendChild(card));
  }

  // Position grid media for collections using page-number pagination (no <collection-load-more>).
  // Called on initial DOMContentLoaded AND after every AJAX filter/sort update so that
  // grid media items are always positioned correctly regardless of how the page was loaded.
  function positionSinglePageMedia() {
    // When collection-load-more is present, connectedCallback runs the full pipeline.
    if (document.querySelector('collection-load-more')) return;

    // Single-page: count actual color-split product-card elements and update toolbar count.
    // For multi-page collections this is handled by _fetchColorCardTotal instead.
    const loadMoreEl = document.querySelector('collection-load-more');
    const productList = document.querySelector('[id^="product-list-"]');
    const countEl = document.querySelector('[id^="products-count-"]');
    const colorSplitEnabled = loadMoreEl
      ? loadMoreEl.dataset.colorSplit === 'true'
      : productList?.dataset.colorSplit === 'true';

    if (colorSplitEnabled && productList && countEl && countEl.dataset.count) {
      const colorSplitTotal = productList.querySelectorAll('product-card').length;
      const countStr = String(colorSplitTotal);
      if (countStr !== countEl.dataset.count) {
        countEl.textContent = countEl.textContent.replace(countEl.dataset.count, countStr);
        countEl.dataset.count = countStr;
      }
    }

    if (!productList) return;

    const enableReorder = productList.dataset.enableReorder === 'true';
    const inStockCards = [...productList.querySelectorAll('product-card:not([hidden]):not([data-sold-out="true"])')];
    if (enableReorder) {
      inStockCards.sort((a, b) => parseInt(a.style.order || '0', 10) - parseInt(b.style.order || '0', 10));
    }

    productList.querySelectorAll('.collection-grid-media[data-position]').forEach(media => {
      const pos = parseInt(media.dataset.position, 10);
      if (isNaN(pos) || pos < 1) return;
      const nthCard = inStockCards[pos - 1];
      if (!nthCard) return;

      const cardOrder = parseInt(nthCard.style.order || '0', 10);
      if (enableReorder) {
        media.style.order = String(cardOrder + 1);
      } else {
        media.style.removeProperty('order');
        nthCard.insertAdjacentElement('afterend', media);
      }
      media.style.display = '';
      media.style.transition = 'opacity 0.25s ease';
      requestAnimationFrame(() => { media.style.opacity = '1'; });
    });

    // Page-number pagination has no load-more buffer — move sold-out to DOM end when
    // manual CSS order is not active (non-manual sort collections).
    if (!enableReorder && !inStockFilterActive()) relocateSoldOutCardsToEnd(productList);
  }

  // Initial page load
  document.addEventListener('DOMContentLoaded', positionSinglePageMedia);

  // After an AJAX filter/sort update: theme:loading:end fires synchronously before
  // shopifySection.replaceChildren(), so setTimeout(0) defers until the new DOM is in place.
  document.documentElement.addEventListener('theme:loading:end', () => {
    setTimeout(positionSinglePageMedia, 0);
  });
})();